import { createReducer, on } from '@ngrx/store';
import { ErrorMessage } from '../error/error-message.model';
import {
	clearCurrentContent,
	clearList,
	clearTemporaryMedia,
	insertCommentFailure,
	insertCommentSuccess,
	insertMediaAndCommentSuccess,
	loadComments,
	loadCommentsFailure,
	loadCommentsSuccess,
	loadContent,
	loadContentFailure,
	loadContentListByCategorySuccess,
	loadContentListFailure,
	loadContentListSuccess,
	loadContentSearchListFailure,
	loadContentSearchListSuccess,
	loadContentSuccess,
	uploadTemporaryMedia,
	uploadTemporaryMediaSuccess
} from './content.actions';
import {
	contentAdapter,
	contentInitialState,
	contentListAdapter,
	contentListInitialState,
	contentListSearchAdapter,
	contentListSearchInitialState
} from './content.entity';
import { DNComment } from './content.model';

export const contentsList = createReducer(
	contentListInitialState,
	// on(clearList, (state) => contentListAdapter.removeAll({ ...state, contentList: null })),
	on(clearList, (state) => ({ ...contentListInitialState })),
	on(loadContentListSuccess, (state, action) =>
		contentListAdapter.setAll(action.contentListData, state)
	),
	on(loadContentListByCategorySuccess, (state, action) =>
		contentListAdapter.setAll(action.contentListData, state)
	),

	on(loadContentListFailure, (state, action) => ({
		...state,
		error: action.error
	}))
);

export const contentsSearchList = createReducer(
	contentListSearchInitialState,
	// on(clearList, (state) => contentListAdapter.removeAll({ ...state, contentList: null })),
	on(clearList, (state) => ({ ...contentListSearchInitialState })),
	on(loadContentSearchListSuccess, (state, action) =>
		contentListSearchAdapter.setAll(action.contentSearchListData, state)
	),
	on(loadContentSearchListFailure, (state, action) => ({
		...state,
		error: action.error
	}))
);

//  Content

export const currentContent = createReducer(
	contentInitialState,
	on(clearCurrentContent, (state) => ({ ...contentInitialState })),
	// check contentId nello store -> se presente, non lo rimuovo
	on(loadContent, (state, { contentId }) =>
		state?.entities?.[state?.ids?.[0]]?.idContenuto !== contentId
			? contentAdapter.removeAll({ ...state, comments: [], error: <ErrorMessage>{} })
			: { ...state, error: <ErrorMessage>{} }
	),
	// quando arriva il content uguale a quello in memoria cambio soltanto l'abilitato
	on(loadContentSuccess, (state, action) => contentAdapter.upsertOne(action.datiContent, state)),
	on(loadContentFailure, (state, action) => ({ ...state, error: action.error })),

	// Comments

	on(loadComments, (state, action) => ({ ...state, error: <ErrorMessage>{} })),
	on(loadCommentsSuccess, (state, action) => {
		// Get parentId from any of the just loaded comments. If there are no comments, use -1 (=> no content with this id)
		const parentId = action?.comments?.[0]?.parentId || -1;

		// Loading 0 level comments
		if (parentId === state.ids[0]) {
			return { ...state, comments: mergeCommentsRoot(state.comments, action.comments) };
		}

		// Copy the current comments state
		const commentsCopy = [...state.comments];

		let newCommentsState = [];

		// Loading n > 0 level comments
		newCommentsState = updateCommentsState(commentsCopy, false, parentId, action.comments, false);
		return { ...state, comments: newCommentsState };
	}),
	on(insertCommentSuccess, (state, action) => {
		// Get parentId from any of the just loaded comments. If there are no comments, use -1 (=> no content with this id)
		const parentId = action?.comments?.[0]?.parentId || -1;

		// Loading 0 level comments
		if (parentId === state.ids[0]) {
			return { ...state, comments: mergeCommentsRoot(state.comments, action.comments) };
		}

		// Copy the current comments state
		const commentsCopy = [...state.comments];

		let newCommentsState = [];

		newCommentsState = updateCommentsState(commentsCopy, false, parentId, action.comments, true);
		return { ...state, comments: newCommentsState };
	}),
	on(insertMediaAndCommentSuccess, (state, action) => {
		// Get parentId from any of the just loaded comments. If there are no comments, use -1 (=> no content with this id)
		const parentId = action?.comments?.[0]?.parentId || -1;

		// Loading 0 level comments
		if (parentId === state.ids[0]) {
			return { ...state, comments: mergeCommentsRoot(state.comments, action.comments) };
		}

		// Copy the current comments state
		const commentsCopy = [...state.comments];

		const newCommentsState = updateCommentsState(
			commentsCopy,
			false,
			parentId,
			action.comments,
			true
		);

		return { ...state, comments: newCommentsState, temporaryMediaName: '' };
	}),
	on(loadCommentsFailure, insertCommentFailure, (state, action) => ({
		...state,
		error: action.error
	})),
	on(uploadTemporaryMedia, (state, action) => ({ ...state, temporaryMediaName: '' })),
	on(uploadTemporaryMediaSuccess, (state, { mediaTemporaryName }) => ({
		...state,
		temporaryMediaName: mediaTemporaryName
	})),
	on(clearTemporaryMedia, (state) => ({ ...state, temporaryMediaName: '' }))
);

/**
 *
 * @param currentRoot The current comments state
 * @param newRoot The new comments tree coming from BE
 * @returns New comments tree with already loaded replies still in it
 */
function mergeCommentsRoot(currentRoot: DNComment[], newRoot: DNComment[]): DNComment[] {
	const newCommentsRoot: DNComment[] = [];

	for (const newComment of newRoot) {
		// Comment to be found into the old root
		// Needed to save new replies into it
		let foundComment = {
			...currentRoot?.find((comment) => comment.idContenuto === newComment.idContenuto)
		};

		// If the comment is found into the old root, copy all the properties of the new comment into the old one
		// This tech is used to copy new replies into old replies property of the comment
		if (foundComment) foundComment = Object.assign(foundComment, newComment);
		// If the comment is not found, then just take the new one
		else foundComment = newComment;

		// In any case, we need to save the new comment into the new root
		newCommentsRoot.push(foundComment);
	}

	return newCommentsRoot;
}

/**
 *
 * @param comments Deep copy of comments state
 * @param found Recursive flag to make the function stop when found
 * @param rootId The parent comment which needs the replies to be updated
 * @param responseReplies The actual replies to put into the comments
 * @returns The new comments state with the replies attached to it
 */
function updateCommentsState(
	comments: DNComment[],
	found: boolean,
	rootId: number,
	responseReplies: DNComment[],
	isInsert: boolean
): DNComment[] {
	if (found) return comments;

	const commentsCopy = [...comments];

	let foundIndex = comments?.findIndex((comment) => comment?.idContenuto === rootId);
	foundIndex = foundIndex === undefined ? -1 : foundIndex;

	if (foundIndex > -1) {
		// Copy replies into field
		commentsCopy[foundIndex] = {
			...commentsCopy[foundIndex],
			replies: mergeCommentsRoot(commentsCopy[foundIndex]?.replies, responseReplies)
		};
		return updateCommentsState(commentsCopy, true, rootId, responseReplies, isInsert);
	} else {
		// Lookup for parent into children
		for (const comment of comments) {
			if (found) break;

			if (comment?.replies && comment?.replies?.length > 0 && !found) {
				const newComments = updateCommentsState(
					comment.replies,
					false,
					rootId,
					responseReplies,
					isInsert
				);

				const updateCommentIndex = comments?.findIndex(
					(findComment) => findComment?.idContenuto === comment.idContenuto
				);

				if (updateCommentIndex > -1) {
					let numRisposteUpdated = commentsCopy[updateCommentIndex].numRisposte;

					if (isInsert) {
						numRisposteUpdated = numRisposteUpdated + 1;
					}

					commentsCopy[updateCommentIndex] = {
						...commentsCopy[updateCommentIndex],
						numRisposte: numRisposteUpdated,
						replies: [...newComments]
					};
				}
			}
		}
	}

	return commentsCopy;
}
